import { useEffect, useRef } from 'react';
const cx = (...values) => values.filter(Boolean).join(' ');
const control = 'control';

const buttonVariants = {
  default: 'button button-default',
  quiet: 'button button-quiet',
  danger: 'button button-danger'
};

export function Button({ variant = 'default', className, ...props }) {
  return <button className={cx(buttonVariants[variant] || buttonVariants.default, className)} {...props} />;
}
export function Input(props) {
  return <input className={cx(control, 'input', props.className)} {...props} />;
}
export function Select({ children, ...props }) {
  return <select className={cx(control, 'select', props.className)} {...props}>{children}</select>;
}
export function Textarea(props) {
  return <textarea className={cx(control, 'textarea', props.className)} {...props} />;
}
export function Checkbox({ label, ...props }) {
  return <label className="check-control"><input type="checkbox" {...props} /><span>{label}</span></label>;
}
export function Switch({ label, ...props }) {
  return <label className="switch-control"><input type="checkbox" role="switch" {...props} /><span>{label}</span></label>;
}
export function Table({ children, ...props }) {
  return <div className="table-scroll"><table className="data-table" {...props}>{children}</table></div>;
}
export function StatusPip({ status, label }) {
  return <span className={`status status-${status}`}><i aria-hidden="true" />{label}</span>;
}
export function Badge({ children, className }) {
  return <span className={cx('badge', className)}>{children}</span>;
}
export function Dialog({ open, title, children, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    const previouslyFocused = document.activeElement;
    const focusables = node?.querySelectorAll('input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])');
    (focusables && focusables[0] ? focusables[0] : node)?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose?.(); }
      if (event.key === 'Tab' && focusables && focusables.length > 0) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
    <section ref={ref} className="dialog" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
      <header className="dialog-header">
        <h2>{title}</h2>
        <button className="button button-quiet" onClick={onClose}>Close</button>
      </header>
      {children}
    </section>
  </div>;
}
export function Sheet({ open, title, children, onClose }) {
  if (!open) return null;
  return <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
    <section className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <header className="panel-heading"><span className="index-label">PANEL / 01</span><button className="button button-quiet" onClick={onClose}>Close</button></header>
      <h2>{title}</h2>{children}
    </section>
  </div>;
}
export function Tabs({ items, value, onChange }) {
  return <div className="tabs" role="tablist">{items.map((item) =>
    <button key={item} role="tab" aria-selected={value === item} className={value === item ? 'tab active' : 'tab'} onClick={() => onChange(item)}>{item}</button>
  )}</div>;
}
export function Toast({ item, onClose }) {
  return (
    <div className={item.kind === 'error' ? 'toast toast-error' : 'toast'} role="status" aria-live="polite" data-toast>
      <span className="toast-message">{item.message}</span>
      <button className="button button-quiet" onClick={onClose}>Dismiss</button>
    </div>
  );
}
export function Tooltip({ label, children }) {
  return <span className="tooltip-wrap" tabIndex="0" aria-label={label}>{children}<span role="tooltip">{label}</span></span>;
}
export function EmptyState({ index = 'EMPTY / 01', title, detail, action }) {
  return <div className="empty-state"><span className="index-label">{index}</span><h2>{title}</h2><p>{detail}</p>{action}</div>;
}
export function Skeleton({ className = '' }) {
  return <span className={cx('skeleton', className)} aria-hidden="true" />;
}
export function NavigationRail({ expanded, items, active, onSelect }) {
  return <nav className={cx('rail', expanded && 'rail-expanded')} aria-label="Main navigation">
    {items.map(({ id, label, short }) => <button key={id} className={active === id ? 'rail-item active' : 'rail-item'} aria-current={active === id ? 'page' : undefined} aria-label={label} onClick={() => onSelect(id)}>
      <span className="rail-index">{short}</span><span className="rail-label">{label}</span>
    </button>)}
  </nav>;
}
/**
 * style.md §7 — the "device bezel": a thin `--ink` surround whose mono caption
 * names the class and true pixel size (e.g. `TABLET 1280×800`). `children` is the
 * panel content (the A5b.10 preview canvas or an ESP32 frame), authored at the
 * real `panelWidth`×`panelHeight`; it is scaled down to `fit` px wide and the
 * caption always reports the real size.
 */
export function DeviceFrame({ label, panelWidth, panelHeight, fit = 420, children, className = '' }) {
  const scale = panelWidth ? fit / panelWidth : 1;
  return <figure className={cx('device-frame', className)}>
    <div className="device-frame-viewport" style={{ width: `${fit}px`, height: `${Math.round(panelHeight * scale)}px` }}>
      <div className="device-frame-scale" style={{ width: panelWidth, height: panelHeight, transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
    <figcaption className="device-frame-caption mono">{label}</figcaption>
  </figure>;
}